import React from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { FileText, ImageIcon, Download, Trash2, Loader2 } from 'lucide-react';
import {
  useQualityEvidencias,
  useDeleteQualityEvidencia,
  getSignedEvidenciaUrl,
  TIPO_DOCUMENTO_OPTIONS,
  type QualityEvidencia,
} from '@/hooks/useQualityEvidencias';
import { useToast } from '@/hooks/use-toast';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useAuth } from '@/contexts/AuthContext';

const fmtSize = (b: number) =>
  b < 1024 ? `${b} B` : b < 1024 * 1024 ? `${(b / 1024).toFixed(1)} KB` : `${(b / 1024 / 1024).toFixed(1)} MB`;

const tipoLabel = (t: string) =>
  TIPO_DOCUMENTO_OPTIONS.find((o) => o.value === t)?.label || t;

interface Props {
  execucaoId: string | null;
  /** Allow deletion (only for pending executions and only by uploader). */
  allowDelete?: boolean;
  emptyMessage?: string;
}

export function QualityEvidenceList({ execucaoId, allowDelete, emptyMessage }: Props) {
  const { data: evidencias = [], isLoading } = useQualityEvidencias(execucaoId);
  const del = useDeleteQualityEvidencia();
  const { toast } = useToast();
  const { user } = useAuth();

  const handleDownload = async (ev: QualityEvidencia) => {
    try {
      const url = await getSignedEvidenciaUrl(ev.arquivo_path, 300);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e: any) {
      toast({ title: 'Falha ao gerar link', description: e.message, variant: 'destructive' });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground py-2">
        <Loader2 className="h-3 w-3 animate-spin" /> Carregando evidências…
      </div>
    );
  }

  if (evidencias.length === 0) {
    return emptyMessage ? (
      <p className="text-xs text-muted-foreground italic py-1">{emptyMessage}</p>
    ) : null;
  }

  return (
    <div className="space-y-1">
      {evidencias.map((ev) => {
        const isImage = ev.mime_type.startsWith('image/');
        const canDelete = allowDelete && ev.uploaded_by === user?.id;
        return (
          <div
            key={ev.id}
            className="flex items-center gap-2 border rounded-md px-2 py-1.5 text-xs bg-muted/20"
          >
            {isImage ? (
              <ImageIcon className="h-3.5 w-3.5 text-primary shrink-0" />
            ) : (
              <FileText className="h-3.5 w-3.5 text-primary shrink-0" />
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="font-medium truncate">{ev.nome_arquivo}</span>
                <Badge variant="outline" className="text-[10px] py-0 h-4">
                  {tipoLabel(ev.tipo_documento)}
                </Badge>
                <span className="text-muted-foreground text-[10px]">{fmtSize(ev.tamanho_bytes)}</span>
              </div>
              {ev.descricao && (
                <p className="text-[11px] text-muted-foreground truncate">{ev.descricao}</p>
              )}
              <p className="text-[10px] text-muted-foreground">
                {format(parseISO(ev.uploaded_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
              </p>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-6 w-6 shrink-0"
              onClick={() => handleDownload(ev)}
              aria-label="Baixar"
            >
              <Download className="h-3.5 w-3.5" />
            </Button>
            {canDelete && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-6 w-6 shrink-0 text-destructive hover:text-destructive"
                onClick={() => del.mutate(ev)}
                disabled={del.isPending}
                aria-label="Remover"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        );
      })}
    </div>
  );
}
